"use client";

import React, { useRef, useState, ReactElement, HTMLAttributes } from "react";
import { Tooltip, type TooltipProps }  from "@/components/controls/base-controls";

// 🔑 Extend standard Tooltip properties, making 'title' mandatory and 'children' explicit
interface ConditionalTooltipProps extends Omit<TooltipProps, "children"> {
  title: React.ReactNode;
  children: ReactElement<HTMLAttributes<HTMLElement> & React.RefAttributes<HTMLElement>>;
}

const ConditionalTooltip = ({ title, children, ...props }: ConditionalTooltipProps) => {
  
  const textRef = useRef<HTMLElement | null>(null);
  const [isTruncated, setIsTruncated] = useState<boolean>(false);

  const handleMouseEnter = () => {
    const element = textRef.current;
    if (element) {
      // 🔑 If scrollWidth > clientWidth, text is clipped and showing ellipses
      setIsTruncated(element.scrollWidth > element.clientWidth);
    }
  };

  return (
    <Tooltip title={title} disableHoverListener={!isTruncated} {...props}>
      {/* Clone the child element to attach the ref and mouse listener */}
      {React.cloneElement(children, {
        ref: (node: HTMLElement | null) => {
          textRef.current = node;
          // 💡 Forward the ref if the child component already has an existing ref
          const { ref: childRef } = children as any;
          if (typeof childRef === "function") {
            childRef(node);
          } else if (childRef) {
            childRef.current = node;
          }
        },
        onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
          handleMouseEnter();
          // 💡 Preserves any existing mouse entry events bound to the child component
          if (children.props.onMouseEnter) {
            children.props.onMouseEnter(e);
          }
        },
    })}
    </Tooltip>
  );
}

export default ConditionalTooltip;
